"use client";

import { useState, useMemo } from "react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import type { CalendarEvent } from "./calendar";
import Pagination from "../table/pagination-table";
import { CalendarEventActions } from "./calendar-event-actions";

interface GridViewProps {
  events: CalendarEvent[];
  onViewEvent?: (event: CalendarEvent) => void;
  onEditEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  refetch?: () => void;
  access?: string;
  hideEventActions?: boolean;
  // Server-side pagination props (all required)
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  isLoading: boolean;
  onLimitChange: (limit: number) => void;
  onNextPage: () => void;
  onPrevPage: () => void;
  onFirstPage: () => void;
  onLastPage: () => void;
  isLastPage: boolean;
}

export function GridView({
  events,
  onViewEvent,
  onEditEvent,
  onDeleteEvent,
  refetch,
  access,
  hideEventActions,
  totalItems,
  currentPage,
  itemsPerPage,
  isLoading,
  onLimitChange,
  onNextPage,
  onPrevPage,
  onFirstPage,
  onLastPage,
  isLastPage,
}: GridViewProps) {
  console.log("GridView Debug:", {
    totalItems,
    currentPage,
    itemsPerPage,
    eventsLength: events.length,
    isLoading,
    isLastPage
  });

  return (
    <div className="absolute inset-0 flex flex-col overflow-hidden">
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 justify-start items-start gap-4 p-4 overflow-y-auto">
        {isLoading ? (
          <div className="col-span-full flex justify-center items-center py-12">
            <div className="flex space-x-2">
              <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="size-4 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="size-4 bg-primary rounded-full animate-bounce"></div>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <div className="text-center text-gray-500">
              <p className="text-lg">No bookings found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          </div>
        ) : (
          events.map((event, index) => {
            try {
              return (
                <div
                  key={event.id}
                  className="p-4 max-h-32 rounded-md border transition-colors bg-blue-50 border-blue-200 hover:bg-blue-100"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 cursor-pointer" onClick={() => {
                      console.log("Event clicked:", event.title);
                      onViewEvent?.(event);
                    }}>
                      <h3 className="font-medium">{event.title}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-gray-500">
                        {(() => {
                          try {
                            return `${format(
                              new Date(event.start),
                              "h:mm a"
                            )} - ${format(new Date(event.end), "h:mm a")}`;
                          } catch (error) {
                            console.error("Error formatting time:", error);
                            return "Time unavailable";
                          }
                        })()}
                      </div>
                      {!hideEventActions && (
                        <CalendarEventActions
                          event={event}
                          onView={onViewEvent}
                          onEdit={onEditEvent}
                          onDelete={onDeleteEvent}
                          refetch={refetch}
                          access={access}
                          size="sm"
                          variant="dropdown"
                        />
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mb-2">
                    {(() => {
                      try {
                        return format(
                          new Date(event.start),
                          "EEEE, MMMM d, yyyy"
                        );
                      } catch (error) {
                        console.error("Error formatting date:", error);
                        return "Date unavailable";
                      }
                    })()}
                  </div>

                  {event.tags && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {event.tags.map((tag, idx) => {
                        // Map colors to static classes to avoid dynamic class generation issues
                        const colorClass =
                          {
                            yellow: "bg-yellow-500",
                            orange: "bg-orange-500",
                            green: "bg-green-500",
                            red: "bg-red-500",
                            blue: "bg-blue-500",
                            gray: "bg-gray-500",
                          }[tag.color] || "bg-gray-500";

                        return (
                          <span
                            key={idx}
                            className={cn(
                              "px-1.5 py-0.5 rounded-sm text-white text-xs",
                              colorClass
                            )}
                          >
                            {tag.label}
                          </span>
                        );
                      })}
                    </div>
                  )}

                  {event.user && (
                    <div className="flex justify-end items-center mt-2">
                      <div className="h-6 w-6 mr-2 bg-gray-300 rounded-full" />
                      <span className="text-xs">{event.user.name}</span>
                    </div>
                  )}
                </div>
              );
            } catch (renderError) {
              return (
                <div
                  key={event.id}
                  className="p-4 bg-red-100 border border-red-300"
                >
                  Error rendering event: {event.title}
                </div>
              );
            }
          })
        )}
      </div>

      {(totalItems > 0 || events.length > 0) && (
        <div className="sticky bottom-0 z-10 py-2 px-4 border-t bg-white">
          <Pagination
            limit={itemsPerPage}
            offset={currentPage}
            totalItems={totalItems}
            onLimitChange={onLimitChange}
            onNextPage={onNextPage}
            onPrevPage={onPrevPage}
            onFirstPage={onFirstPage}
            onLastPage={onLastPage}
            isLastPage={isLastPage}
          />
        </div>
      )}
    </div>
  );
}
